import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { UserService } from 'app/core/services/user.service';
import { NotifMessageService } from 'app/core/services/notifmessage.service';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, Observable, forkJoin, concat, of } from 'rxjs';
import { User } from 'app/core/models/user';
import { map } from 'rxjs/operators';
import { ConfigurationService } from 'app/core/services/configuration.service';
import { Roles } from 'app/core/models/roles';
import { Supplier } from 'app/core/models/supplier';
import { Merchant } from 'app/core/models/merchant';
import { SupplierService } from 'app/core/services/supplier.service';
import { MerchantService } from 'app/core/services/merchant.service';
import { AuthenticatedRouteGuard } from 'security/authenticatedRouteGuard.service';
import { KeycloakService } from 'security/keycloak.service';
import { BaseComponent } from 'app/base/base.component';

@Component({
  selector: 'app-edit-user',
  templateUrl: './edit-user.component.html',
  styleUrls: ['./edit-user.component.css']
})
export class EditUserComponent extends BaseComponent implements OnInit {

  isLoading: boolean = true;
  isSaveNeeded: boolean = false;
  userForm: FormGroup;
  editedUser: User;
  subRoute: Subscription;
  supplierSub: Subscription;
  merchantSub: Subscription;

  selectableRoles: Array<Roles> = [];
  assignedRoles: Array<Roles> = [];
  initialRoles: Array<Roles> = [];

  suppliers: Array<Supplier> = [];
  merchants: Array<Merchant> = [];
  allSuppliers: Array<Supplier>;
  allMerchants: Array<Merchant>;
  allSuppliersInit: Array<Supplier>;
  allMerchantsInit: Array<Merchant>;
  supplierControl: FormControl = new FormControl();
  merchantControl: FormControl = new FormControl();

  isSupplierAdding: boolean = false;
  isMerchantAdding: boolean = false;

  isMerchantRead: boolean = false;
  isSupplierRead: boolean = false;

  isLinked: boolean = false;

  canLinkToMerchant: boolean = false;
  canLinkToSupplier: boolean = false;

  needLink: boolean = true;

  localeItems = [
    { value: "fr", label: "LANGUAGE_FRENCH_FRANCE" },
    { value: "en", label: "LANGUAGE_ENGLISH_UNITED_STATES" }
  ]

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private notifService: NotifMessageService,
    public router: Router,
    private route: ActivatedRoute,
    private configService: ConfigurationService,
    private supplierService: SupplierService,
    private merchantService: MerchantService,
    private roleGuard: AuthenticatedRouteGuard,
    private location: Location,
    private keycloakService: KeycloakService,
    private translateService: TranslateService) { super(router) }

  ngOnInit() {
    this.subRoute = this.route.params.subscribe(params => {
      this.userService.getUserById(params['id']).subscribe(user => {
        this.editedUser = user;
        this.initForm();
        this.isLoading = false;
        this.keycloakService.rolesObservable.subscribe(roles => {
          if (roles.length !== 0) {
            this.getRoles();
            concat(
              this.getMerchants(),
              this.getSuppliers(),
            ).subscribe({
              next: () => console.log("get entities done"),
              complete: () => {
                if (this.roleGuard.isEnabled('ADMIN_MERCHANT')) {
                  this.canLinkToMerchant = true;
                }

                if (this.roleGuard.isEnabled('ADMIN_SUPPLIER')) {
                  this.canLinkToSupplier = true;
                }
              }
            });
          }
        });
      });
    });
  }

  ngOnDestroy() {
    if (this.merchantSub) {
      this.merchantSub.unsubscribe();
    }
    if (this.supplierSub) {
      this.supplierSub.unsubscribe();
    }
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.isSaveNeeded) {
      this.notifService.emitModal("SAVE_NEEDED");
      return this.notifService.modalResponseSubject$;
    } else {
      return true;
    }
  }

  get user() {
    return this.userForm.controls;
  }

  getRoles() {
    forkJoin(
      this.getSelectableRoles(),
      this.getAssignedRoles()
    ).subscribe(([sRoles, aRoles]) => {
      if (sRoles.length > 0) {
        sRoles.forEach(role => {
          this.selectableRoles.push(role = { ...role, display: this.translateService.instant(role.name) });
        });
      }
      if (aRoles.length > 0) {

        aRoles.forEach(role => {
          if (this.configService.config.security.keycloak.roles.includes(role.name) && !this.selectableRoles.some(r => r.name === role.name)) {
            this.selectableRoles.push(role = { ...role, display: this.translateService.instant(role.name) });
          }
        })

        if (aRoles.some(role => role.name === "ADMIN_WORLDLINE")) {
          aRoles.forEach(role => {
            if (role.name === "ADMIN_WORLDLINE" && this.configService.config.security.keycloak.roles.includes(role.name)) {
              this.assignedRoles.push(role = { ...role, display: this.translateService.instant(role.name) });
            }
          });
        } else {
          aRoles.forEach(role => {
            if (this.configService.config.security.keycloak.roles.includes(role.name)) {
              this.assignedRoles.push(role = { ...role, display: this.translateService.instant(role.name) });
            }
          });
        }
        this.initialRoles = Object.assign([], this.assignedRoles);
        this.checkRoles();
      }
    });
  }

  getSelectableRoles(): Observable<Array<Roles>> {
    return this.userService.getAllRoles().pipe(
      map(roles => {
        return roles.filter(role => {
          let roleEnabled: boolean = false;
          if (this.roleGuard.isEnabled("ADMIN_WORLDLINE")) {
            roleEnabled = this.configService.config.security.keycloak.roles.includes(role.name) && role.name !== "ADMIN_WORLDLINE";
          } else {
            if (this.roleGuard.isEnabled("ADMIN_MERCHANT_PETROL")) {
              roleEnabled = this.configService.config.security.keycloak.roles.includes(role.name) && (role.name.endsWith("PETROL") || role.name.startsWith("VIEWER"));
            }
            if (this.roleGuard.isEnabled("ADMIN_MERCHANT_OPH")) {
              roleEnabled = this.configService.config.security.keycloak.roles.includes(role.name) && (role.name.endsWith("OPH") || role.name.startsWith("VIEWER"));
            }
            if (this.roleGuard.isEnabled("ADMIN_SUPPLIER")) {
              roleEnabled = this.configService.config.security.keycloak.roles.includes(role.name) && (role.name.endsWith("SUPPLIER") || role.name.startsWith("VIEWER"));
            }
            if (this.roleGuard.isEnabled("ADMIN_SUPPLIER") && this.roleGuard.isEnabled("ADMIN_MERCHANT_PETROL")) {
              roleEnabled = this.configService.config.security.keycloak.roles.includes(role.name) && (role.name.endsWith("PETROL") || role.name.endsWith("SUPPLIER") || role.name.startsWith("VIEWER"));
            }
            if (this.roleGuard.isEnabled("ADMIN_SUPPLIER") && this.roleGuard.isEnabled("ADMIN_MERCHANT_OPH")) {
              roleEnabled = this.configService.config.security.keycloak.roles.includes(role.name) && (role.name.endsWith("OPH") || role.name.endsWith("SUPPLIER") || role.name.startsWith("VIEWER"));
            }
          }
          return roleEnabled;
        })
      })
    );
  }

  getAssignedRoles(): Observable<Array<Roles>> {
    return this.userService.getRoles(this.editedUser);
  }

  initForm() {
    let locale: string;
    if (this.editedUser.attributes == undefined) {
      locale = "en";
    } else {
      locale = this.editedUser.attributes.locale[0];
    }
    this.userForm = this.formBuilder.group({
      username: new FormControl(this.editedUser.username, Validators.required),
      firstName: new FormControl(this.editedUser.firstName, Validators.compose([
        Validators.required,
        Validators.pattern(this.contactNameRegex)
      ])),
      lastName: new FormControl(this.editedUser.lastName, Validators.compose([
        Validators.required,
        Validators.pattern(this.contactNameRegex)
      ])),
      email: new FormControl(this.editedUser.email, Validators.compose([
        Validators.required,
        Validators.pattern(this.emailRegex)
      ])),
      locale: new FormControl(locale, Validators.required)
    });

    this.userForm.valueChanges.subscribe(() => {
      this.isSaveNeeded = true;
    })
  }

  addRoles(user, assignedRoles): Observable<any> {
    let roles: Array<Roles> = [];
    assignedRoles.forEach(role => {
      delete role.display
      roles.push(role);
    });
    return this.userService.addRoles(user, roles);
  }

  removeRoles(user): Observable<any> {
    if (this.initialRoles.length > 0) {
      let roles: Array<Roles> = [];
      this.initialRoles.forEach(role => {
        delete role.display
        roles.push(role);
      });
      return this.userService.removeRoles(user, this.initialRoles);
    } else {
      return of("No roles to remove");
    }
  }

  onSubmitForm() {
    this.isSaveNeeded = false;
    let attributes: {
      locale: Array<string>;
      merchantId?: Array<number>;
      supplierId?: Array<number>;
    } = {
      locale: this.userForm.get("locale").value
    };
    if (!this.roleGuard.isEnabled('SUPPLIER_READ')) {
      attributes = { ...attributes, supplierId: this.editedUser.attributes.supplierId };
    } else if (this.suppliers.length > 0) {
      attributes = { ...attributes, supplierId: [this.suppliers[0].id] };

    }
    if (!this.roleGuard.isEnabled('MERCHANT_READ')) {
      attributes = { ...attributes, merchantId: this.editedUser.attributes.merchantId };
    } else if (this.merchants.length > 0) {
      attributes = { ...attributes, merchantId: [this.merchants[0].id] };
    }
    let newUser : User = {
      id: this.editedUser.id,
      username: this.userForm.get("username").value,
      firstName: this.userForm.get("firstName").value,
      lastName: this.userForm.get("lastName").value,
      email: this.userForm.get("email").value,
      enabled: true,
      attributes: attributes
    };

    this.userService.isEmailExists(newUser.email, newUser.id).subscribe(res => {
      if (res) {
        this.notifService.notifyWarn('USER_EMAIL_EXIST');
      } else {
        this.userService.updateUser(newUser).pipe(map(() => "updated")).subscribe(
          res => {
            let rolesUpdated = false;
            let rolesRemoved = false;
            concat(
              this.removeRoles(newUser).pipe(map(() => "roles removed")),
              this.addRoles(newUser, this.assignedRoles).pipe(map(() => "roles updated"))
            ).subscribe(res => {
              if (res === "roles updated") {
                this.notifService.notifySuccess('ROLES_UPDATED');
                rolesUpdated = true;
              } else if (res === "roles removed") {
                rolesRemoved = true;
              }
              if (rolesRemoved && rolesUpdated) {
                this.router.navigate(['/user']);
                this.notifService.notifySuccess('USER_UPDATED');
              }
            });
          }
        )
      }
    }
    );
  }

  getSuppliers() {
    if (this.roleGuard.isEnabled('SUPPLIER_READ')) {

      if (this.editedUser.attributes && this.editedUser.attributes.supplierId && this.editedUser.attributes.supplierId[0]) {
        this.supplierService.getSupplierById(this.editedUser.attributes.supplierId[0]).subscribe(supplier => {
          if (supplier.id) {
            this.suppliers.splice(0, this.suppliers.length);
            this.suppliers.push(supplier);
          }
          this.checkLinked();
        })
      }

      return this.supplierService.getAllSuppliers().pipe(map(suppliers => {
        this.allSuppliers = suppliers.content;
        this.allSuppliersInit = suppliers.content;
        if (this.allSuppliers && this.allSuppliers.length > 0) {
          this.supplierControl = new FormControl(this.allSuppliers[0].id);
          this.isSupplierRead = true;
        }
      }));
    } else {
      return of(null);
    }
  }

  getMerchants() {
    if (this.roleGuard.isEnabled('MERCHANT_READ')) {

      if (this.editedUser.attributes && this.editedUser.attributes.merchantId && this.editedUser.attributes.merchantId[0]) {
        this.merchantService.getMerchantById(this.editedUser.attributes.merchantId[0]).subscribe(merchant => {
          if (merchant.id) {
            this.merchants.splice(0, this.merchants.length);
            this.merchants.push(merchant);
          }
          this.checkLinked();
        })
      }

      return this.merchantService.getAllMerchants().pipe(map(merchants => {
        this.allMerchants = merchants.content;
        this.allMerchantsInit = merchants.content;
        if (this.allMerchants && this.allMerchants.length > 0) {
          this.merchantControl = new FormControl(this.allMerchants[0].id);
          this.isMerchantRead = true;
        }
      }));

    } else {
      return of(null);
    }
  }

  addMerchant() {
    let indexTab: Array<number> = [];
    this.allMerchants = Object.assign([], this.allMerchantsInit);
    this.allMerchants.forEach(merchant => {
      if (this.merchants.includes(merchant)) {
        let index = this.allMerchants.findIndex(mer => mer == merchant);
      }
    });
    this.allMerchants = this.allMerchants.filter((supp, i) => !indexTab.includes(i));
    this.merchantControl = new FormControl(this.allMerchants[0].id);
    this.isMerchantAdding = true;
    this.isSaveNeeded = true;
  }

  addSupplier() {
    let indexTab: Array<number> = [];
    this.allSuppliers = Object.assign([], this.allSuppliersInit);
    this.allSuppliers.forEach(supplier => {
      if (this.suppliers.includes(supplier)) {
        indexTab.push(this.allSuppliers.findIndex(supp => supp == supplier));
      }
    });
    this.allSuppliers = this.allSuppliers.filter((supp, i) => !indexTab.includes(i));
    this.supplierControl = new FormControl(this.allSuppliers[0].id);
    this.isSupplierAdding = true;
    this.isSaveNeeded = true;
  }

  cancel() {
    this.isSupplierAdding = false;
    this.isMerchantAdding = false;
  }

  linkSupplier() {
    let supplier = this.allSuppliers.find(supp => supp.id == this.supplierControl.value);
    this.suppliers.push(supplier);
    this.isSupplierAdding = false;
    this.isSaveNeeded = true;
    this.checkLinked();
  }

  removeSupplier(id) {
    let index = this.suppliers.findIndex(supplier => supplier.id === id);
    this.suppliers.splice(index, 1);
    this.isSupplierAdding = false;
    this.isSaveNeeded = true;
    this.checkLinked();
  }

  linkMerchant() {
    let merchant = this.allMerchants.find(mer => mer.id == this.merchantControl.value);
    this.merchants.push(merchant);
    this.isMerchantAdding = false;
    this.isSaveNeeded = true;
    this.checkLinked();
  }

  removeMerchant(id) {
    let index = this.merchants.findIndex(merchant => merchant.id === id);
    this.merchants.splice(index, 1);
    this.isMerchantAdding = false;
    this.isSaveNeeded = true;
    this.checkLinked();
  }

  checkLinked() {
    if (this.suppliers.length > 0 || this.merchants.length > 0) {
      this.isLinked = true;
    } else {
      this.isLinked = false;
    }
  }

  back() {
    this.location.back();
  }

  cancelForm() {
    this.location.back();
  }

  isLinkedToEntity(): boolean {
    if (this.assignedRoles.some(role => role.name == "ADMIN_WORLDLINE")) {
      return true;
    } else if (this.isLinked) {
      return true;
    } else {
      return false;
    }
  }

  checkRoles() {
    if (this.assignedRoles.some(role => role.name === "ADMIN_WORLDLINE")) {
      this.needLink = false;
    } else {
      this.needLink = true;
    }
    if (this.assignedRoles.length !== this.initialRoles.length) {
      this.isSaveNeeded = true;
    }
  }

  transSearchFn = (term: string, item: any) => {
    term = term.toLocaleLowerCase();
    return this.translateService.instant(item.label).toLocaleLowerCase().indexOf(term) > -1;
  }
}
